import { useMemo } from "react";

import { ListAllDestinationsForEnvResponse, useApiQuery } from "@/api";
import {
  SaaSDestination,
  slugForDestination,
} from "@/destinations/destinations";
import {
  DestinationValidity,
  getDestinationValidity,
} from "@/destinations/validation";
import useCurrentProject from "@/hooks/use-project";
import useAccountMetadata from "@/hooks/useAccountMetadata";
import useAllSaasDestinationsByType from "@/hooks/useAllSaasDestinationsByType";
import { LoadedDestinationInfo } from "@/types/global";

export default function useListAllDestinationsForEnv(
  onSuccess: (destinations: LoadedDestinationInfo[]) => void = () => {},
) {
  const accountMetadata = useAccountMetadata();
  const currentProject = useCurrentProject();
  const saasDestinations = useAllSaasDestinationsByType();
  const { data: consentManagementConfigsData } = useApiQuery([
    "get_consent_management_configs",
    {},
  ]);

  // Memoize the select function so it only changes when its dependencies change.
  // This prevents unnecessary recalculations when React Query's internal
  // memoization compares function references.
  // See: https://tkdodo.eu/blog/react-query-data-transformations
  const select = useMemo(
    () => (data: ListAllDestinationsForEnvResponse) => {
      const resolvedDestinations = data?.destinations
        ? data.destinations.map((destination) => {
            const saasDestination = saasDestinations[destination.name];
            if (!saasDestination) {
              // This case is most likely for warehouse destinations that do not
              // use the resolved validity system.
              return {
                ...destination,
                type_slug: slugForDestination(destination),
                // We can assume validity here as we have no way to check
                // otherwise.
                resolvedValidity: { validity: DestinationValidity.valid },
              };
            }

            return {
              ...destination,
              type_slug: slugForDestination(destination),
              resolvedValidity:
                !accountMetadata || !currentProject.loaded
                  ? // default validity was defined as valid elsewhere
                    // we may want to later display a pending status state
                    { validity: DestinationValidity.valid }
                  : getDestinationValidity(
                      accountMetadata,
                      currentProject,
                      destination,
                      saasDestination as SaaSDestination,
                      consentManagementConfigsData?.configs,
                    ),
            };
          })
        : data.destinations;

      return { destinations: resolvedDestinations };
    },
    [
      accountMetadata,
      currentProject,
      saasDestinations,
      consentManagementConfigsData,
    ],
  );

  // Note: The select type is cast because useApiQuery's typing doesn't properly
  // support the select option's transformation signature.
  return useApiQuery(["list_all_destinations_for_env", {}], {
    select: select as (data: object) => ListAllDestinationsForEnvResponse,
    onSuccess: (data) => {
      onSuccess(data.destinations);
    },
  });
}
